"use client"
import { Button } from "@/components/ui/button"
import { Trash2, BookOpen, ChevronRight } from "lucide-react"
import { Link } from "react-router"
import { CareerIllustration } from "@/components/ui/career-explorer/CareerIllustration"

interface CareerPathCardProps {
  id: string
  category: string
  categoryColor: string
  title: string
  description: string
  broadCategory?: string
  progress: number
  focusLabel?: string | null
  onExplore?: () => void
  onDelete?: (id: string) => void
}

export function CareerPathCard({
  id,
  category,
  categoryColor,
  title,
  description,
  broadCategory,
  progress,
  focusLabel,
  onExplore,
  onDelete,
}: CareerPathCardProps) {
  return (
    <div className="flex flex-col rounded-xl bg-slate-100 shadow-md transition-shadow hover:shadow-lg">
      {/* Illustration header */}
      <div className="relative rounded-t-xl overflow-hidden h-36">
        <CareerIllustration nodeId={id} broadCategory={broadCategory} className="w-full h-full" />
        {/* Delete Button */}
        <button
          onClick={() => onDelete?.(id)}
          className="absolute top-3 right-3 flex size-8 items-center justify-center rounded-full bg-white/50 text-slate-700 backdrop-blur-sm transition hover:bg-white/80 hover:text-red-500"
        >
          <Trash2 className="w-5 h-5" />
        </button>
        {/* Focus label badge — shown when student selected a specific field/role */}
        {focusLabel && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1">
            <ChevronRight className="w-3 h-3 text-white/70 shrink-0" />
            <p className="text-[11px] font-semibold text-white truncate max-w-45">{focusLabel}</p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Category */}
        <p className={`text-sm font-medium ${categoryColor}`}>{category}</p>

        {/* Title */}
        <p className="text-lg font-bold text-slate-900">{title}</p>

        {/* Description */}
        <p className="mt-1 flex-1 text-sm text-slate-500">{description}</p>

        {/* Progress Section */}
        <div className="mt-4 flex flex-col gap-2">
          <div className="flex justify-between text-xs font-medium text-slate-500">
            <span>Your Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-200">
            <div
              className="h-2 rounded-full bg-yellow-400 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Buttons Container */}
        <div className="mt-4 space-y-2">
          {/* Explore Path Button */}
          <Link to={`/career-learning-path?id=${id}`} className="w-full block">
            <Button
              onClick={onExplore}
              className="w-full bg-blue-600/10 hover:bg-blue-600 text-emerald-800 hover:text-white text-sm font-bold rounded-lg h-10 transition-all duration-200"
            >
              Explore Path
            </Button>
          </Link>

          {/* My Learning Journey Button */}
          <Link to="/curriculum" className="w-full block">
            <Button
              className="w-full bg-emerald-50 hover:bg-emerald-500 text-emerald-600 hover:text-white border border-emerald-200 hover:border-emerald-500 text-sm font-medium rounded-lg h-10 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Continue Journey
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}